import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from '../../theme';
import { mergeUserViewportTheme } from '../../theme/userViewportTheme';
import appVersionManager from '../../utils/appVersion';

const ColorModeContext = createContext();

export const useColorMode = () => {
    const context = useContext(ColorModeContext);
    if (!context) {
        throw new Error('useColorMode must be used within a ColorModeProvider');
    }
    return context;
};

export const ColorModeProvider = ({ children, appType = 'admin' }) => {
    // Get device's preferred color scheme
    const getDeviceColorScheme = () => {
        // First try to get from native app
        if (appVersionManager.isInAppEnvironment()) {
            const nativeColorScheme = appVersionManager.getDeviceColorScheme();
            if (nativeColorScheme) {
                return nativeColorScheme;
            }
        }

        // Fallback to web browser detection
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
        }
        return 'light'; // final fallback
    };

    // Get platform-specific default
    const getPlatformDefault = () => {
        if (typeof window !== 'undefined') {
            const userAgent = window.navigator.userAgent.toLowerCase();
            const isAndroid = userAgent.indexOf('android') > -1;
            const isIOS = /iphone|ipad|ipod/.test(userAgent);

            // Mobile devices (Android/iOS) default to dark mode for better battery life
            if (isAndroid || isIOS) {
                return 'dark';
            }
        }
        return 'light'; // Desktop default
    };

    // Get app type specific default
    const getAppTypeDefault = () => {
        switch (appType) {
            case 'user':
                return 'dark'; // User app defaults to dark mode
            case 'admin':
            default:
                return 'light'; // Admin app defaults to light mode
        }
    };

    // Initialize with priority: saved mode > device preference > platform default > app type default
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('colorMode');
        if (savedMode) {
            return savedMode;
        }

        // Try device preference first
        const devicePreference = getDeviceColorScheme();
        if (devicePreference) {
            return devicePreference;
        }

        // Fall back to platform default
        const platformDefault = getPlatformDefault();
        if (platformDefault) {
            return platformDefault;
        }

        // Final fallback to app type default
        return getAppTypeDefault();
    });

    // Listen for device color scheme changes
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mediaQuery = window.matchMedia(
                '(prefers-color-scheme: dark)'
            );

            const handleChange = e => {
                // Only update if no saved preference exists
                const savedMode = localStorage.getItem('colorMode');
                if (!savedMode) {
                    const newMode = e.matches ? 'dark' : 'light';
                    setMode(newMode);
                }
            };

            mediaQuery.addEventListener('change', handleChange);

            return () => {
                mediaQuery.removeEventListener('change', handleChange);
            };
        }
    }, []);

    // Listen for app version updates (including device color scheme from native app)
    useEffect(() => {
        const handleAppVersionUpdate = event => {
            const { colorScheme } = event.detail;
            if (colorScheme) {
                // Only update if no saved preference exists
                const savedMode = localStorage.getItem('colorMode');
                if (!savedMode) {
                    setMode(colorScheme);
                }
            }
        };

        window.addEventListener('appVersionUpdated', handleAppVersionUpdate);

        return () => {
            window.removeEventListener(
                'appVersionUpdated',
                handleAppVersionUpdate
            );
        };
    }, []);

    // localStorage saving will be implemented later

    const toggleColorMode = () => {
        setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const setColorMode = newMode => {
        if (['light', 'dark'].includes(newMode)) {
            setMode(newMode);
        }
    };

    const theme = useMemo(() => {
        const base = mode === 'light' ? lightTheme : darkTheme;
        return appType === 'user' ? mergeUserViewportTheme(base) : base;
    }, [mode, appType]);

    const value = {
        mode,
        toggleColorMode,
        setColorMode,
        isLight: mode === 'light',
        isDark: mode === 'dark',
    };

    return (
        <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default ColorModeContext;
